import React from 'react';

import { WidgetsMapComponentKeys, widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import { Link } from 'react-router-dom';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseWidget } from 'hooks/useBaseWidget';

import { AssigneeAvatar } from 'components/common/AssigneeAvatar/AssigneeAvatar';
import { CustomizedStatusSelectWrapper } from 'components/common/CustomizedStatusSelect/CustomizedStatusSelectWrapper/CustomizedStatusSelectWrapper';
import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetExistInBackend } from 'components/common/WidgetExistInBackend/WidgetExistInBackend';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';
import { InformationBoxes } from 'components/common/informationBoxes/informationBoxes';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { CustomizedTooltip, LoaderContainer } from 'components/common';
import { useFinishDateOverallSummaryLogic } from 'components/widgets/FinishDateOverallSummary/FinishDateOverallSummary.logic';
import classes from 'components/widgets/FinishDateOverallSummary/FinishDateOverallSummary.module.scss';
import { useFinishDateOverallSummaryProvider } from 'components/widgets/FinishDateOverallSummary/FinishDateOverallSummary.provider';
import {
    BoxLabels,
    displayProbability,
    formatVarianceString,
} from 'components/widgets/FinishDateOverallSummary/FinishDateOverallSummary.utils';

const { componentKey, id, endpoint } = widgetsConfigurations.finishDateOverallSummary;

export const FinishDateOverallSummary = ({
    projectId,
    contractId,
    latestVersionId,
    globalFilterData,
    widgetId,
    localFilters,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,

    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title = widgetsConfigurations.finishDateOverallSummary.title,
}: IComponentProps) => {
    const { ui, api } = useFinishDateOverallSummaryProvider({
        widgetId,
        globalFilterData,
        localFilters,
        latestVersionId,
        componentKey,
        widgetViewMode,
    });

    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        apiPayload: api.getActivityPrediction.payload,
        widgetEndpoint: endpoint,
    });

    const { logic } = useFinishDateOverallSummaryLogic({ api });

    const activityPredictionData = api.getActivityPrediction.instance.data;
    const isFetchingActivityPrediction = api.getActivityPrediction.instance.isFetching;

    return (
        <WidgetExistInBackend componentKey={componentKey}>
            <WidgetWithTitle
                id={id}
                title={
                    <WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />
                }
                className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                titleComponents={[
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <AddWidget
                        key={'AddWidget'}
                        componentKey={componentKey}
                        title={title}
                        projectId={projectId}
                        contractId={contractId}
                        widgetId={widgetId}
                        route={route}
                        localFilters={ui.state}
                        shouldHide={true}
                        shouldUseNewIcon={true}
                    />,
                    <AddToReport
                        key={'addWidgetToReport'}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        projectId={projectId}
                        contractId={contractId}
                        uiState={ui.state}
                        shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                        apiPayload={api.getActivityPrediction.payload}
                        widgetEndpoint={endpoint}
                    />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ExportDropdown key={'export'} title={title} subTitle={subTitle} />
                    ),
                    <TooltipIcon key={'info'} tooltip={api.getActivityPrediction.instance.data?.info} />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ThreeDotsMenu
                            widgetViewMode={widgetViewMode}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            shareTitle={title}
                            shareLink={route}
                            projectId={projectId}
                            contractId={contractId}
                        />
                    ),
                ]}
                titleFilters={[
                    <ReportingVersionAndPills
                        key={'ReportingVersionAndPills'}
                        selectedProjectAndContract={widgetSelectedProjectAndContract}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    <Narrative key={'narrative'} editNarrative={editNarrative} />,
                    <PinboardPills
                        key={'pinboardPills'}
                        isInPinboard={ui.isInPinboard}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                ]}
            >
                {isFetchingActivityPrediction && (
                    <LoaderContainer height={'120px'}>
                        <OverlayWithSpinner />
                    </LoaderContainer>
                )}
                {!isFetchingActivityPrediction && !activityPredictionData && (
                    <NoData>
                        To view prediction for the <strong>Project Finish</strong> activity select the{' '}
                        <strong>first</strong> activity in filter list above.
                        <br />
                        To view prediction for a <strong>specific</strong> milestone/activity select it from the filter
                        list above.
                    </NoData>
                )}

                {!isFetchingActivityPrediction &&
                    activityPredictionData &&
                    !activityPredictionData.assignee &&
                    !activityPredictionData.status &&
                    !activityPredictionData.activityId &&
                    !activityPredictionData.name && <NoData />}

                {!isFetchingActivityPrediction &&
                    activityPredictionData &&
                    activityPredictionData.assignee &&
                    activityPredictionData.status &&
                    activityPredictionData.activityId &&
                    activityPredictionData.name && (
                        <InformationBoxes className={`${classes.row}`}>
                            <InformationBoxes.Box className={classes.twiceTheSize}>
                                <div data-testid={'summary-activity-box'} className={classes.activityBox}>
                                    <div className={classes.activitySmallTitle}>{BoxLabels.activity}</div>
                                    <div className={classes.activityContainer}>
                                        <div className={classes.activityParent}>
                                            <div className={classes.activityNameDetails}>
                                                <CustomizedTooltip
                                                    tooltipContent={activityPredictionData.name}
                                                    triggerElement={
                                                        activityPredictionData.taskId ? (
                                                            <Link
                                                                to={{
                                                                    pathname: `${logic.pathname}/card-${activityPredictionData.taskId}${activityPredictionData.isVirtual ? '-1' : ''}`,
                                                                }}
                                                                className={classes.activityLink}
                                                                data-testid={'activity-link'}
                                                            >
                                                                <div className={classes.tooltipContentParent}>
                                                                    <div className={classes.tooltipContent}>
                                                                        {activityPredictionData.name}
                                                                    </div>
                                                                </div>
                                                            </Link>
                                                        ) : (
                                                            activityPredictionData.name
                                                        )
                                                    }
                                                />
                                            </div>
                                            <div className={classes.status}>
                                                <CustomizedStatusSelectWrapper
                                                    disabled={true}
                                                    status={activityPredictionData.status}
                                                    originalStatus={null}
                                                    taskId={activityPredictionData.taskId}
                                                    isVirtual={activityPredictionData.isVirtual}
                                                    invalidateQueriesKey={['activityPrediction']}
                                                />
                                                <AssigneeAvatar assignee={activityPredictionData.assignee} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className={classes.activityId}>{activityPredictionData.activityId}</div>
                                </div>
                            </InformationBoxes.Box>

                            <InformationBoxes.Box className={classes.plannedFinish}>
                                <div className={`${classes.box}`}>
                                    <div className={classes.top}>
                                        <div className={classes.smallTitle}>{BoxLabels.currentPlannedFinish}</div>
                                        <div
                                            data-testid={'data-value-container'}
                                            className={classes.dataValueContainer}
                                        >
                                            <div className={classes.title}>
                                                <div className={classes.predictionProbability}>
                                                    {logic.plannedFinishDate}
                                                    <CustomizedTooltip
                                                        tooltipPosition={TooltipPosition.Right}
                                                        tooltipContent={'Likelihood of meeting this date'}
                                                        triggerElement={`(${displayProbability(activityPredictionData.probability)}%)`}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div data-testid={'baseline-finish-date'} className={classes.baselineFinish}>
                                        Baseline Finish{' '}
                                        {`${logic.baselineFinishDate} ${formatVarianceString(activityPredictionData?.baselineFinishVariance)}`}
                                    </div>
                                </div>
                            </InformationBoxes.Box>

                            <InformationBoxes.Box className={classes.optimisticForecast}>
                                <div className={classes.box}>
                                    <div className={classes.top}>
                                        <div className={classes.smallTitle}>{BoxLabels.optimisticForecast}</div>
                                        <div
                                            data-testid={'data-value-container'}
                                            className={classes.dataValueContainer}
                                        >
                                            <div className={classes.title}>
                                                {`${logic.optimisticForecastedData} ${formatVarianceString(activityPredictionData?.optimisticFinishVariance)}`}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </InformationBoxes.Box>

                            <InformationBoxes.Box className={classes.bestForecast}>
                                <div className={classes.box}>
                                    <div className={classes.top}>
                                        <div className={classes.bestForecastTitle}>{BoxLabels.mostLikelyForecast}</div>
                                        <div
                                            data-testid={'data-value-container'}
                                            className={classes.dataValueContainer}
                                        >
                                            <div
                                                className={classes.bestForecastSmallTitle}
                                            >{`${logic.bestForecastedData} ${formatVarianceString(activityPredictionData?.bestFinishVariance)}`}</div>
                                        </div>
                                    </div>
                                </div>
                            </InformationBoxes.Box>

                            <InformationBoxes.Box className={classes.pessimisticForecast}>
                                <div className={classes.box}>
                                    <div className={classes.top}>
                                        <div className={classes.smallTitle}>{BoxLabels.pessimisticForecast}</div>
                                        <div
                                            data-testid={'data-value-container'}
                                            className={classes.dataValueContainer}
                                        >
                                            <div
                                                className={classes.title}
                                            >{`${logic.pessimisticForecasted} ${formatVarianceString(activityPredictionData?.pessimisticsFinishVariance)}`}</div>
                                        </div>
                                    </div>
                                </div>
                            </InformationBoxes.Box>
                        </InformationBoxes>
                    )}
            </WidgetWithTitle>
        </WidgetExistInBackend>
    );
};
